import React, { useEffect, useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import { VERSION_CHECK_INTERVAL_MS, fetchServerBuild } from '@/hooks/useNewVersion';
import { reloadPage } from '@/utils/reload-once';

/**
 * Zwei Anlässe innerhalb dieser Frist sind einer: Beim Wechsel zurück in den
 * Reiter melden Browser „sichtbar“ und „Fokus“ fast gleichzeitig.
 */
const DOPPELT_MS = 2_000;

/**
 * Der Build auf dem Server, sobald er von dem der laufenden Oberfläche
 * abweicht — sonst `null`.
 *
 * Gefragt wird beim Start (eine zwischengespeicherte alte index.html fällt so
 * sofort auf), wenn der Reiter wieder sichtbar wird oder das Fenster den Fokus
 * bekommt, und alle fünf Minuten. version.json kommt ohne Zwischenspeicher
 * (no-store, .htaccess) und ist ein paar Byte groß; Fehler bleiben still —
 * ohne Antwort weiß die Oberfläche nichts Neues.
 */
export function useServerBuildChange(eigenerBuild: string = __APP_BUILD__): string | null {
  const [neuerBuild, setNeuerBuild] = useState<string | null>(null);

  useEffect(() => {
    // Im Entwicklungsserver gibt es keine version.json, und einmal erkannt
    // bleibt der Hinweis stehen — weiteres Fragen ändert nichts.
    if (neuerBuild || (import.meta.env.DEV && import.meta.env.MODE !== 'test')) return undefined;
    let aktiv = true;
    let zuletzt = 0;
    const pruefe = async () => {
      const jetzt = Date.now();
      if (zuletzt && jetzt - zuletzt < DOPPELT_MS) return;
      zuletzt = jetzt;
      const build = await fetchServerBuild();
      if (aktiv && build && build !== eigenerBuild) setNeuerBuild(build);
    };
    const beiFokus = () => void pruefe();
    const beiSichtbarkeit = () => {
      if (document.visibilityState === 'visible') void pruefe();
    };

    void pruefe();
    window.addEventListener('focus', beiFokus);
    document.addEventListener('visibilitychange', beiSichtbarkeit);
    const takt = window.setInterval(() => void pruefe(), VERSION_CHECK_INTERVAL_MS);
    return () => {
      aktiv = false;
      window.removeEventListener('focus', beiFokus);
      document.removeEventListener('visibilitychange', beiSichtbarkeit);
      window.clearInterval(takt);
    };
  }, [eigenerBuild, neuerBuild]);

  return neuerBuild;
}

/**
 * Der Hinweis auf eine neue Version.
 *
 * Bis 0.3.0 stand er klein unten links („Eine neue Version von revilion ist
 * verfügbar.“) und wurde im Lauf vom 31.10.2026 nicht bemerkt: Nach dem
 * Einspielen um 08:18 Uhr liefen die Schritte bis 10:01 Uhr mit der alten
 * Oberfläche (Build 20261026.2306), und im Protokoll ist kein Hinweis erfasst
 * (Testbericht TG 2.3, 4.2 Nr. 9). Jetzt steht er als Band über der ganzen
 * Breite, nennt den neuen Build und sagt, was zu tun ist.
 *
 * Er bleibt stehen, bis neu geladen wird, und lädt nicht von selbst: Wer mitten
 * in einer Eingabe ist, soll sie erst speichern können.
 */
export const NewVersionNotice: React.FC = () => {
  const neuerBuild = useServerBuildChange();
  if (!neuerBuild) return null;

  return (
    <Box
      data-testid="new-version-banner"
      sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: (theme) => theme.zIndex.snackbar + 1 }}
    >
      <Alert
        severity="warning"
        variant="filled"
        square
        sx={{ alignItems: 'center', boxShadow: 3 }}
        action={
          <Button color="inherit" variant="outlined" size="small" onClick={reloadPage}>
            Neu laden
          </Button>
        }
      >
        Eine neue Version von revilion ist eingespielt (Build {neuerBuild}). Bitte laden Sie die Seite neu, bevor
        Sie weiterarbeiten.
      </Alert>
    </Box>
  );
};
